import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Globe, Server, Mail, LogOut, TrendingUp, Calendar, ShoppingBag, AlertCircle, Check, Bell } from "lucide-react";
import { toast } from "sonner";

const SAR = (n) => `${(n || 0).toLocaleString("en-US")}`;

const StatusBadge = ({ status }) => {
    const map = {
        active: { bg: "bg-redsea-500", fg: "text-white", label: "Active" },
        expiring_soon: { bg: "bg-souq-400", fg: "text-ink-900", label: "Expiring" },
        expired: { bg: "bg-ink-500", fg: "text-white", label: "Expired" },
        pending: { bg: "bg-mudbrick-400", fg: "text-white", label: "Pending" },
    };
    const s = map[status] || map.pending;
    return (
        <span className={`inline-flex items-center gap-1 rounded-[6px] px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider ${s.bg} ${s.fg}`}>
            {status === "active" && <Check className="h-3 w-3" strokeWidth={3} />}
            {status === "expiring_soon" && <AlertCircle className="h-3 w-3" />}
            {s.label}
        </span>
    );
};

const daysUntil = (iso) => {
    if (!iso) return 0;
    return Math.round((new Date(iso) - Date.now()) / (1000 * 60 * 60 * 24));
};

export default function Dashboard() {
    const { user, logout, authAxios } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await authAxios.get("/user/services");
                setData(data);
            } catch {
                toast.error("Could not load services");
            } finally {
                setLoading(false);
            }
        })();
    }, [authAxios]);

    if (loading || !data) {
        return (
            <main className="min-h-screen bg-limestone-100 pt-32 px-5 flex items-center justify-center" data-testid="dashboard-loading">
                <div className="h-10 w-10 animate-spin rounded-full border-4 border-ink-200 border-t-madder-500" />
            </main>
        );
    }

    const { domains, dns_zones, mailboxes, summary } = data;
    const expiringSoon = domains.filter((d) => {
        const days = daysUntil(d.expires_at);
        return d.status !== "expired" && days >= 0 && days <= 30;
    });

    return (
        <main className="min-h-screen bg-limestone-100 pt-28 pb-24" data-testid="customer-dashboard">
            {/* Asir stripe */}
            <div className="asir-stripe h-[6px]" aria-hidden="true" />

            <div className="mx-auto max-w-[1320px] px-5 pt-12 lg:px-8">
                {/* Renewal alert — award-winning retention UX */}
                {expiringSoon.length > 0 && (
                    <div
                        className="mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 rounded-card border-2 border-madder-500 bg-madder-50 p-5"
                        data-testid="renewal-alert-banner"
                    >
                        <div className="flex items-start gap-3">
                            <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-[10px] bg-madder-500 text-white">
                                <Bell className="h-5 w-5" strokeWidth={2.4} />
                            </span>
                            <div>
                                <p className="font-display text-[18px] font-bold text-ink-800">
                                    {expiringSoon.length === 1
                                        ? `1 domain is expiring in ${daysUntil(expiringSoon[0].expires_at)} days`
                                        : `${expiringSoon.length} domains are expiring within 30 days`}
                                </p>
                                <p className="mt-0.5 text-[14px] font-semibold text-madder-700">
                                    Renew today to avoid losing <span className="font-mono num-ltr">{expiringSoon.map((d) => d.domain).join(", ")}</span>.
                                </p>
                            </div>
                        </div>
                        <button
                            type="button"
                            data-testid="renewal-alert-cta"
                            className="inline-flex h-[48px] items-center gap-2 rounded-btn bg-madder-500 px-5 text-[15px] font-bold text-white hover:bg-madder-600"
                        >
                            Renew now
                        </button>
                    </div>
                )}

                {/* Header */}
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <p className="eyebrow">Customer · {user.email}</p>
                        <h1 className="section-headline mt-3" style={{ fontSize: "clamp(36px, 4.4vw, 56px)" }}>
                            Welcome back, <span className="text-madder-500">{user.name}</span>
                        </h1>
                        <p className="mt-3 text-[17px] font-medium text-ink-600">
                            Here's every service running under your account.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Link
                            to="/home-a#hero-search"
                            data-testid="dashboard-buy-more"
                            className="inline-flex h-[48px] items-center gap-2 rounded-btn bg-madder-500 px-5 text-[15px] font-bold text-white hover:bg-madder-600"
                        >
                            <ShoppingBag className="h-4 w-4" />
                            Buy more
                        </Link>
                        <button
                            type="button"
                            onClick={logout}
                            data-testid="dashboard-logout"
                            className="inline-flex h-[48px] items-center gap-2 rounded-btn border-2 border-ink-300 bg-white px-4 text-[15px] font-bold text-ink-700 hover:border-ink-800 hover:text-ink-800"
                        >
                            <LogOut className="h-4 w-4" />
                            Sign out
                        </button>
                    </div>
                </div>

                {/* Summary stat cards */}
                <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-4">
                    {[
                        { label: "Domains", value: summary.domains_count, icon: Globe, bg: "bg-madder-500", fg: "text-white" },
                        { label: "DNS Zones", value: summary.dns_zones_count, icon: Server, bg: "bg-redsea-500", fg: "text-white" },
                        { label: "Mailboxes", value: summary.mailboxes_count, icon: Mail, bg: "bg-souq-400", fg: "text-ink-900" },
                        { label: "Annual Spend", value: `${SAR(summary.active_spend_sar)} SAR`, icon: TrendingUp, bg: "bg-ink-800", fg: "text-white", small: true },
                    ].map((s) => (
                        <div key={s.label} className={`rounded-card p-6 ${s.bg} ${s.fg}`} data-testid={`summary-${s.label.toLowerCase().replace(' ', '-')}`}>
                            <div className="flex items-center justify-between">
                                <s.icon className="h-5 w-5" />
                                <p className="text-[11px] font-bold uppercase tracking-[0.18em] opacity-70">{s.label}</p>
                            </div>
                            <p className={`mt-5 font-display font-extrabold leading-none tabular-nums num-ltr ${s.small ? "text-[28px]" : "text-[44px]"}`}>
                                {s.value}
                            </p>
                        </div>
                    ))}
                </div>

                {/* Domains table */}
                <section className="mt-14" data-testid="dashboard-domains">
                    <div className="flex items-center justify-between mb-5">
                        <h2 className="font-display text-[28px] font-bold tracking-tight text-ink-800">My Domains</h2>
                        <p className="font-mono text-[13px] font-semibold text-ink-600 num-ltr">{domains.length} total</p>
                    </div>
                    <div className="overflow-hidden rounded-card border-2 border-ink-200 bg-white">
                        <div className="hidden md:grid grid-cols-12 gap-4 border-b-2 border-ink-200 bg-mudbrick-100 px-6 py-3 font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-ink-700">
                            <div className="col-span-4">Domain</div>
                            <div className="col-span-2">Status</div>
                            <div className="col-span-3">Expires</div>
                            <div className="col-span-2">Price</div>
                            <div className="col-span-1 text-end">Renew</div>
                        </div>
                        {domains.map((d) => {
                            const days = daysUntil(d.expires_at);
                            return (
                                <div key={d.reg_id} className="grid grid-cols-1 md:grid-cols-12 items-center gap-4 border-b border-ink-100 px-6 py-5 last:border-b-0 hover:bg-mudbrick-100/50" data-testid={`domain-row-${d.reg_id}`}>
                                    <div className="col-span-4">
                                        <p className="font-mono text-[17px] font-bold text-ink-800 num-ltr">{d.domain}</p>
                                        <p className="font-mono text-[12px] text-ink-500 num-ltr">{d.tld}</p>
                                    </div>
                                    <div className="col-span-2"><StatusBadge status={d.status} /></div>
                                    <div className="col-span-3 flex items-center gap-2 text-[14px] font-semibold text-ink-700">
                                        <Calendar className="h-4 w-4 text-ink-400" />
                                        <span className="tabular-nums num-ltr">{(d.expires_at || "").slice(0, 10)}</span>
                                        <span className={`font-mono text-[11px] font-bold ${days < 30 ? "text-madder-500" : "text-ink-500"}`}>({days}d)</span>
                                    </div>
                                    <div className="col-span-2 font-mono text-[15px] font-bold tabular-nums num-ltr text-ink-800">{d.price_sar} SAR</div>
                                    <div className="col-span-1 text-end">
                                        <span className={`inline-flex items-center gap-1 rounded-[4px] px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider ${d.auto_renew ? "bg-redsea-500 text-white" : "bg-ink-200 text-ink-700"}`}>
                                            {d.auto_renew ? "ON" : "OFF"}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                        {domains.length === 0 && (
                            <div className="p-10 text-center">
                                <p className="text-[16px] font-semibold text-ink-600">No domains yet. Start by searching for one.</p>
                                <Link to="/home-a#hero-search" className="mt-4 inline-flex h-[48px] items-center gap-2 rounded-btn bg-madder-500 px-5 text-[15px] font-bold text-white">Search for a domain</Link>
                            </div>
                        )}
                    </div>
                </section>

                {/* DNS zones + Mailboxes — two columns */}
                <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-2">
                    <section data-testid="dashboard-dns">
                        <h2 className="mb-5 font-display text-[24px] font-bold tracking-tight text-ink-800">DNS Zones</h2>
                        <div className="overflow-hidden rounded-card border-2 border-ink-200 bg-white">
                            {dns_zones.map((z) => (
                                <div key={z.zone_id} className="flex items-center justify-between border-b border-ink-100 px-6 py-4 last:border-b-0 hover:bg-mudbrick-100/50">
                                    <div>
                                        <p className="font-mono text-[16px] font-bold text-ink-800 num-ltr">{z.domain}</p>
                                        <p className="mt-0.5 text-[13px] font-semibold text-ink-600">{z.records_count} records · propagation {z.propagation}</p>
                                    </div>
                                    <StatusBadge status={z.status} />
                                </div>
                            ))}
                            {dns_zones.length === 0 && <p className="p-6 text-[15px] text-ink-500">No DNS zones yet.</p>}
                        </div>
                    </section>

                    <section data-testid="dashboard-mailboxes">
                        <h2 className="mb-5 font-display text-[24px] font-bold tracking-tight text-ink-800">Mailboxes</h2>
                        <div className="overflow-hidden rounded-card border-2 border-ink-200 bg-white">
                            {mailboxes.map((m) => (
                                <div key={m.mailbox_id} className="flex items-center justify-between border-b border-ink-100 px-6 py-4 last:border-b-0 hover:bg-mudbrick-100/50">
                                    <div>
                                        <p className="font-mono text-[16px] font-bold text-ink-800 num-ltr">{m.primary_address}</p>
                                        <p className="mt-0.5 text-[13px] font-semibold text-ink-600">{m.count} accounts · {m.storage_gb} GB</p>
                                    </div>
                                    <span className="inline-flex items-center gap-1 rounded-[4px] bg-ink-800 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-white">DKIM ✓</span>
                                </div>
                            ))}
                            {mailboxes.length === 0 && <p className="p-6 text-[15px] text-ink-500">No mailboxes yet.</p>}
                        </div>
                    </section>
                </div>
            </div>
        </main>
    );
}
